import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import { issueTypeStatusSortFn } from "@overmap-ai/common"
import { useSort, useTextFilter } from "@overmap-ai/hooks"
import { memo, type ReactElement } from "react"

import { issueTypeStatusFilterCallback } from "../../filtering/utils"
import { modelIconMapping } from "../../mappings/icons"
import {
	IssueTypeStatusSelectGroup,
	type IssueTypeStatusSelectGroupProps,
} from "../MenuSelectGroups/IssueTypeStatusSelectGroup"

type IssueTypeStatusSelectProps = Omit<MenuRootProps, "children"> &
	Omit<MenuClickTriggerProps, "children"> &
	MenuVariantProps &
	IssueTypeStatusSelectGroupProps & {
		children: ReactElement
	}

export const IssueTypeStatusSelect = memo<IssueTypeStatusSelectProps>((props) => {
	const {
		// Trigger props
		children,
		disabled = false,
		// MenuContent props
		size,
		radius,
		variant,
		accentColor,
		// Status props
		issueTypeStatuses,
		required,
		defaultValue,
		value,
		onValueChange,
		// Rest props
		...rest
	} = props

	const sortedIssueTypeStatuses = useSort(issueTypeStatuses, issueTypeStatusSortFn)

	const [filteredIssueTypeStatuses, filterValue, setFilterValue] = useTextFilter(
		sortedIssueTypeStatuses,
		issueTypeStatusFilterCallback,
	)

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content size={size} radius={radius} variant={variant} accentColor={accentColor}>
				<Menu.InputRoot>
					<Menu.InputSlot>
						<LuIcon icon={modelIconMapping.search} />
					</Menu.InputSlot>
					<Menu.InputField placeholder="Search" value={filterValue} onValueChange={setFilterValue} />
				</Menu.InputRoot>
				{filteredIssueTypeStatuses.length > 0 && <Menu.Separator />}
				<Menu.Scroll>
					{required ? (
						<IssueTypeStatusSelectGroup
							issueTypeStatuses={filteredIssueTypeStatuses}
							required={required}
							value={value}
							defaultValue={defaultValue}
							onValueChange={onValueChange}
						/>
					) : (
						<IssueTypeStatusSelectGroup
							issueTypeStatuses={filteredIssueTypeStatuses}
							required={required}
							value={value}
							defaultValue={defaultValue}
							onValueChange={onValueChange}
						/>
					)}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
IssueTypeStatusSelect.displayName = "IssueTypeStatusSelect"
